import { selectCurrentQuestionIndex } from '../providers/questionnaireEngine.selectors'
import { getAnswerableQuestionsAndPrune, getLinkIdOfQuestionnaireQuestion } from '../utils/questionnaire-engine.utils'
import { validateQuestionnaireAnswers } from '../utils/questionnaire-evaluation.utils'
import { buildHealthParameterDependencyMap } from '../utils/questionnaire-health-parameters.utils'

import type { IQuestionnaireQuestion, QuestionAnswerInput, SubjectMember } from '@healthblocks-io/core'
import type { QuestionnaireEngineState } from '../types/questionnaire-engine.types'

type DerivedState = {
  currentIndex: number
  totalQuestions: number
  canSubmit: boolean
}

export type QuestionnaireFlowStrategy = {
  findNext(state: QuestionnaireEngineState): string | null
  isComplete(state: QuestionnaireEngineState, derived: DerivedState): boolean
  saveBehavior: 'on-navigate' | 'on-complete'
  completeAction: 'submit' | 'save'
  resolveAutoAdvance(
    state: QuestionnaireEngineState,
    subject: SubjectMember,
    answer: QuestionAnswerInput,
    derived: DerivedState,
  ): { isComplete: boolean; nextLinkId: string | null }
}

// ── Linear strategy (normal questionnaire flow) ─────────────────────────────

export const linearStrategy: QuestionnaireFlowStrategy = {
  findNext(state) {
    const currentIndex = selectCurrentQuestionIndex(state)
    if (currentIndex === -1) return null
    const next = state.answerableQuestions[currentIndex + 1]
    if (!next) return null
    return getLinkIdOfQuestionnaireQuestion(next)
  },

  isComplete(_state, derived) {
    return derived.currentIndex === derived.totalQuestions - 1
  },

  saveBehavior: 'on-navigate',
  completeAction: 'submit',

  resolveAutoAdvance(state, _subject, _answer, derived) {
    if (derived.currentIndex === derived.totalQuestions - 1) {
      return { isComplete: true, nextLinkId: null }
    }

    const currentIndex = selectCurrentQuestionIndex(state)
    const next = state.answerableQuestions[currentIndex + 1]
    return {
      isComplete: false,
      nextLinkId: next ? getLinkIdOfQuestionnaireQuestion(next) : null,
    }
  },
}

// ── Invalid-first strategy (reconfirm + prefill flows) ──────────────────────

export const invalidFirstStrategy: QuestionnaireFlowStrategy = {
  findNext(state) {
    return findNextInvalidQuestionKey(state)
  },

  isComplete(_state, derived) {
    return derived.canSubmit
  },

  saveBehavior: 'on-complete',
  completeAction: 'save',

  resolveAutoAdvance(state, subject, answer) {
    return previewStateAfterAnswer(state, subject, answer)
  },
}

// ── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Scan questions for the first invalid one, starting after startIndex and
 * wrapping around to the beginning if needed.
 */
function findFirstInvalidLinkId(
  questions: readonly IQuestionnaireQuestion[],
  validations: Record<string, { isValid: boolean }>,
  startIndex: number,
): string | null {
  for (let index = startIndex + 1; index < questions.length; index++) {
    const q = questions[index]
    if (q.type === 'Display') continue
    const linkId = getLinkIdOfQuestionnaireQuestion(q)
    if (!(validations[linkId]?.isValid ?? false)) return linkId
  }

  for (let index = 0; index < startIndex; index++) {
    const question = questions[index]
    if (question.type === 'Display') continue
    const linkId = getLinkIdOfQuestionnaireQuestion(question)
    if (!(validations[linkId]?.isValid ?? false)) return linkId
  }

  return null
}

function findNextInvalidQuestionKey(state: QuestionnaireEngineState): string | null {
  const currentIndex = selectCurrentQuestionIndex(state)
  if (currentIndex === -1) return null

  return findFirstInvalidLinkId(state.answerableQuestions, state.validations, currentIndex)
}

function previewStateAfterAnswer(
  state: QuestionnaireEngineState,
  subject: SubjectMember,
  answer: QuestionAnswerInput,
): { isComplete: boolean; nextLinkId: string | null } {
  const nextAnswers = { ...state.answers, [answer.linkId]: answer }

  // In reconfirm mode, clear dependent health parameter answers when the
  // anchor observation changes — mirrors buildSetAnswerState in the reducer.
  // Without this, dependents keep their old valid answers and the auto-advance
  // skips past them.
  if (state.mode === 'reconfirm') {
    const { anchorToDependents } = buildHealthParameterDependencyMap(state.questionnaire)
    const dependents = anchorToDependents.get(answer.linkId)
    if (dependents) {
      for (const depLinkId of dependents) {
        delete nextAnswers[depLinkId]
      }
    }
  }

  const { answerableQuestions } = getAnswerableQuestionsAndPrune(state.questionnaire, nextAnswers, subject)

  const validations = validateQuestionnaireAnswers(answerableQuestions, nextAnswers, subject)

  const isComplete = Object.values(validations).every((v) => v.isValid)
  if (isComplete) return { isComplete: true, nextLinkId: null }

  const currentIndex = selectCurrentQuestionIndex(state)
  const nextLinkId = findFirstInvalidLinkId(answerableQuestions, validations, currentIndex)

  return { isComplete: false, nextLinkId }
}
